import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useChurch } from "@/hooks/useChurch";
import { useAuth } from "@/hooks/useAuth";
import { useFreemium } from "@/hooks/useFreemium";
import { usePermissions } from "@/hooks/usePermissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import TableFilterBar from "@/components/table/TableFilterBar";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Plus, Pencil, Trash2, PiggyBank, TrendingUp, Scale, Search, FolderKanban, MoreHorizontal } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { sortGeneralFirstByName } from "@/lib/utils";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useNavigate } from "react-router-dom";

export interface ProjectBudget {
  id: string;
  church_id: string;
  name: string;
  description: string | null;
  total_budget: number;
  start_date: string | null;
  end_date: string | null;
  status: string;
  organization_id: string | null;
}

type OrganizationOption = { id: string; name: string };

export interface BudgetItem {
  id: string;
  project_budget_id: string;
  church_id: string;
  category: string;
  budgeted_amount: number;
  actual_amount: number;
  notes: string | null;
}

interface Props {
  formatCurrency: (v: number) => string;
}

export default function ProjectBudgets({ formatCurrency }: Props) {
  const { profile } = useChurch();
  const { user } = useAuth();
  const { isPreview, requireSubscription } = useFreemium();
  const { getFinanceOrganizationIds, hasFinanceScopedAccess, isChurchAdmin } = usePermissions();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const financeScoped = !isChurchAdmin && hasFinanceScopedAccess();
  const allowedOrgIds = getFinanceOrganizationIds();
  const [showForm, setShowForm] = useState(false);
  const [editProject, setEditProject] = useState<ProjectBudget | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterMinistry, setFilterMinistry] = useState("all");
  const [viewYear, setViewYear] = useState("all");

  const { data: projects = [], isLoading } = useQuery({
    queryKey: ["project_budgets", profile?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("project_budgets")
        .select("*")
        .eq("church_id", profile!.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as unknown as ProjectBudget[];
    },
    enabled: !!profile?.id,
  });

  const { data: allItems = [] } = useQuery({
    queryKey: ["project_budget_items", profile?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("project_budget_items")
        .select("*")
        .eq("church_id", profile!.id)
        .order("category");
      if (error) throw error;
      return data as BudgetItem[];
    },
    enabled: !!profile?.id,
  });

  const { data: organizations = [] } = useQuery({
    queryKey: ["organizations", "project_budget_scope"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("organizations")
        .select("id, name")
        .eq("church_id", profile!.id)
        .order("name");
      if (error) throw error;
      return sortGeneralFirstByName((data || []) as OrganizationOption[]);
    },
    enabled: !!profile?.id,
  });

  const sortedOrganizations = useMemo(() => sortGeneralFirstByName(organizations), [organizations]);

  const scopedProjects = useMemo(() => {
    if (!financeScoped) return projects;
    return projects.filter((p) => p.organization_id && allowedOrgIds.includes(p.organization_id));
  }, [projects, financeScoped, allowedOrgIds]);

  const deleteProjectMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("project_budgets").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project_budgets"] });
      queryClient.invalidateQueries({ queryKey: ["project_budget_items"] });
      
    },
  });

  const years = useMemo(() => {
    const ys = new Set<string>();
    projects.forEach((p) => {
      if (p.start_date) ys.add(p.start_date.substring(0, 4));
      if (p.end_date) ys.add(p.end_date.substring(0, 4));
    });
    return Array.from(ys).sort().reverse();
  }, [projects]);

  const scopedOrganizations = useMemo(
    () => (financeScoped ? sortedOrganizations.filter((o) => allowedOrgIds.includes(o.id)) : sortedOrganizations),
    [financeScoped, sortedOrganizations, allowedOrgIds]
  );

  const selectedStatusLabel = useMemo(() => {
    if (filterStatus === "all") return "All";
    if (filterStatus === "on_hold") return "On Hold";
    return filterStatus.charAt(0).toUpperCase() + filterStatus.slice(1);
  }, [filterStatus]);

  const selectedMinistryLabel = useMemo(() => {
    if (filterMinistry === "all") return "All Ministries";
    const found = scopedOrganizations.find((o) => o.id === filterMinistry);
    return found?.name || "All Ministries";
  }, [filterMinistry, scopedOrganizations]);

  const selectedYearLabel = useMemo(() => (viewYear === "all" ? "All Years" : viewYear), [viewYear]);

  const projectFilterGroups = useMemo(
    () => [
      {
        key: "status",
        label: "Status",
        options: ["Active", "Completed", "On Hold"],
        selected: filterStatus === "all" ? new Set<string>() : new Set([selectedStatusLabel]),
        onToggle: (value: string) => setFilterStatus(value.toLowerCase().replace(" ", "_")),
        onClear: () => setFilterStatus("all"),
      },
      {
        key: "ministry",
        label: "Ministry",
        options: scopedOrganizations.map((o) => o.name),
        selected: filterMinistry === "all" ? new Set<string>() : new Set([selectedMinistryLabel]),
        onToggle: (value: string) => {
          const found = scopedOrganizations.find((o) => o.name === value);
          setFilterMinistry(found?.id || "all");
        },
        onClear: () => setFilterMinistry("all"),
      },
      {
        key: "year",
        label: "Year",
        options: years,
        selected: viewYear === "all" ? new Set<string>() : new Set([selectedYearLabel]),
        onToggle: (value: string) => setViewYear(value),
        onClear: () => setViewYear("all"),
      },
    ],
    [selectedStatusLabel, scopedOrganizations, selectedMinistryLabel, years, selectedYearLabel]
  );

  const displayedProjects = useMemo(() => {
    return scopedProjects.filter((p) => {
      if (searchQuery && !p.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      if (filterStatus !== "all" && p.status !== filterStatus) return false;
      if (filterMinistry !== "all") {
        if (filterMinistry === "__none__") {
          if (p.organization_id) return false;
        } else {
          if (p.organization_id !== filterMinistry) return false;
        }
      }
      if (viewYear !== "all") {
        const startYear = p.start_date?.substring(0, 4);
        const endYear = p.end_date?.substring(0, 4);
        if (startYear !== viewYear && endYear !== viewYear) return false;
      }
      return true;
    });
  }, [scopedProjects, searchQuery, filterStatus, filterMinistry, viewYear]);

  const statusColors: Record<string, string> = {
    active: "bg-success/10 text-success border-success/30",
    completed: "bg-muted text-muted-foreground border-border",
    on_hold: "bg-warning/10 text-warning border-warning/30",
  };

  return (
    <div className="space-y-4">
      {/* Overall summary cards */}
      {!isLoading && displayedProjects.length > 0 && (() => {
        const grandBudgeted = displayedProjects.reduce((s, p) => s + Number(p.total_budget), 0);
        const displayedProjectIds = new Set(displayedProjects.map((p) => p.id));
        const grandActual = allItems.filter((i) => displayedProjectIds.has(i.project_budget_id)).reduce((s, i) => s + Number(i.actual_amount), 0);
        const grandVariance = grandBudgeted - grandActual;
        return (
          <div className="rounded-3xl border border-[#d7e4f5] bg-gradient-to-br from-white to-[#f4f8ff] p-4 sm:p-5 shadow-[0_8px_30px_-8px_rgba(2,6,23,0.10)]">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
              <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
                <CardContent className="pt-5 pb-5 px-5">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-medium text-muted-foreground">Total Budgeted</p>
                    <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                      <PiggyBank className="w-4.5 h-4.5 text-primary" />
                    </div>
                  </div>
                  <p className="text-2xl font-bold font-heading text-foreground tracking-tight">{formatCurrency(grandBudgeted)}</p>
                  <p className="text-xs text-muted-foreground mt-1">{`${selectedYearLabel} ${selectedMinistryLabel} projects`}</p>
                </CardContent>
              </Card>
              <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
                <CardContent className="pt-5 pb-5 px-5">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-medium text-muted-foreground">Total Actual</p>
                    <div className="w-9 h-9 rounded-lg bg-success/10 flex items-center justify-center">
                      <TrendingUp className="w-4.5 h-4.5 text-success" />
                    </div>
                  </div>
                  <p className="text-2xl font-bold font-heading text-foreground tracking-tight">{formatCurrency(grandActual)}</p>
                  <p className="text-xs text-muted-foreground mt-1">Total Spent</p>
                </CardContent>
              </Card>
              <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
                <CardContent className="pt-5 pb-5 px-5">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-medium text-muted-foreground">Variance</p>
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${grandVariance >= 0 ? "bg-success/10" : "bg-destructive/10"}`}>
                      <Scale className={`w-4.5 h-4.5 ${grandVariance >= 0 ? "text-success" : "text-destructive"}`} />
                    </div>
                  </div>
                  <p className={`text-2xl font-bold font-heading tracking-tight ${grandVariance >= 0 ? "text-success" : "text-destructive"}`}>
                    {formatCurrency(grandVariance)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">{grandVariance >= 0 ? "Under Budget" : "Over Budget"}</p>
                </CardContent>
              </Card>
            </div>
          </div>
        );
      })()}

      <div className="stat-card p-0 overflow-hidden">
        {/* Toolbar */}
        <div className="px-3 py-3 border-b border-border/40">
          <TableFilterBar
            search={searchQuery}
            onSearchChange={setSearchQuery}
            searchPlaceholder="Search project..."
            searchFirstOnMobile
            mobileControlsFirst
            compactDesktopFilters
            filterColumns={2}
            filterGroups={projectFilterGroups}
            extraControls={
              <Button
                size="sm"
                className="h-7 w-7 p-0 text-[11px] sm:h-9 sm:w-auto sm:px-3 sm:text-sm shrink-0"
                onClick={() => {
                  if (!requireSubscription()) return;
                  setEditProject(null);
                  setShowForm(true);
                }}
              >
                {isPreview && <span className="mr-1"></span>}
                <Plus className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1.5" />
                <span className="hidden sm:inline">New Project</span>
              </Button>
            }
          />
        </div>

      <div className="min-h-[420px]">
      {isLoading ? (
        <div className="flex min-h-[420px] items-center justify-center text-muted-foreground">Loading...</div>
      ) : displayedProjects.length === 0 ? (
        <div className="flex min-h-[420px] items-center justify-center text-center px-4">
          <p className="text-muted-foreground">{projects.length === 0 ? "No project budgets yet. Create your first project!" : "No projects match your filters."}</p>
        </div>
      ) : (
        <div className="divide-y divide-border">
          {displayedProjects.map((project) => {
            const items = allItems.filter((i) => i.project_budget_id === project.id);
            const totalActual = items.reduce((s, i) => s + Number(i.actual_amount), 0);
            const pct = Number(project.total_budget) > 0 ? Math.min((totalActual / Number(project.total_budget)) * 100, 100) : 0;
            const ministryName = project.organization_id && organizations.length > 0
              ? organizations.find((o) => o.id === project.organization_id)?.name
              : null;

            return (
              <div key={project.id} className="overflow-hidden">
                <div
                  className="flex flex-col gap-3 px-5 py-3 cursor-pointer hover:bg-secondary/20 transition-colors lg:flex-row lg:items-center lg:justify-between"
                  onClick={() => navigate(`/finance/projects/${project.id}`)}
                >
                  <div className="flex min-w-0 items-start gap-3">
                    <FolderKanban className="mt-0.5 w-4 h-4 text-primary shrink-0" />
                    <div className="min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-semibold text-foreground text-sm truncate">{project.name}</p>
                        <div className="sm:hidden flex items-center gap-1.5 shrink-0" title={`${pct.toFixed(0)}% used`}>
                          <Progress value={pct} className="h-1.5 w-14" />
                          <p className="text-[11px] text-muted-foreground">{pct.toFixed(0)}%</p>
                        </div>
                      </div>
                      <div className="mt-1">
                        <Badge className={`text-xs flex-shrink-0 ${statusColors[project.status] || ""}`}>{project.status}</Badge>
                      </div>
                      {ministryName && <p className="text-xs text-primary/70 mt-0.5 truncate">{ministryName}</p>}
                      {project.description && <p className="text-xs text-muted-foreground mt-0.5 truncate">{project.description}</p>}
                    </div>
                  </div>
                  <div className="hidden sm:flex items-center gap-3 text-sm flex-wrap lg:flex-nowrap lg:ml-4 lg:justify-end">
                    <div className="text-left lg:text-right min-w-[4.75rem]">
                      <p className="text-xs text-muted-foreground">Budget</p>
                      <p className="font-semibold text-foreground">{formatCurrency(Number(project.total_budget))}</p>
                    </div>
                    <div className="text-left lg:text-right min-w-[4.75rem]">
                      <p className="text-xs text-muted-foreground">Spent</p>
                      <p className={`font-semibold ${totalActual > Number(project.total_budget) ? "text-destructive" : "text-foreground"}`}>
                        {formatCurrency(totalActual)}
                      </p>
                    </div>
                    <div className="w-20 lg:w-24">
                      <Progress value={pct} className="h-1.5" />
                      <p className="text-xs text-muted-foreground text-center mt-0.5">{pct.toFixed(0)}%</p>
                    </div>
                    <div className="shrink-0 hidden sm:block" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="p-1 rounded hover:bg-secondary transition-colors">
                            <MoreHorizontal className="w-4 h-4 text-muted-foreground" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => {
                            if (!requireSubscription()) return;
                            setEditProject(project);
                            setShowForm(true);
                          }}>
                            <Pencil className="w-4 h-4 mr-2" /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => {
                            if (!requireSubscription()) return;
                            deleteProjectMutation.mutate(project.id);
                          }}>
                            <Trash2 className="w-4 h-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      </div>
      </div>

      <ProjectForm
        open={showForm}
        onClose={() => { setShowForm(false); setEditProject(null); }}
        project={editProject}
        churchId={profile?.id || ""}
        userId={user?.id || ""}
        organizations={sortedOrganizations}
        scopedOrgIds={financeScoped ? allowedOrgIds : null}
      />

    </div>
  );
}

export function ProjectForm({ open, onClose, project, churchId, userId, organizations, scopedOrgIds }: {
  open: boolean; onClose: () => void; project: ProjectBudget | null; churchId: string; userId: string;
  organizations: OrganizationOption[]; scopedOrgIds: string[] | null;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(project?.name || "");
  const [description, setDescription] = useState(project?.description || "");
  const [totalBudget, setTotalBudget] = useState(project ? Number(project.total_budget).toLocaleString('en-US') : "");
  const [startDate, setStartDate] = useState(project?.start_date || "");
  const [endDate, setEndDate] = useState(project?.end_date || "");
  const [status, setStatus] = useState(project?.status || "active");
  const [organizationId, setOrganizationId] = useState<string>(project?.organization_id || "");

  // Re-populate fields whenever the dialog opens or the project being edited changes
  useEffect(() => {
    if (!open) return;
    if (project) {
      setName(project.name || "");
      setDescription(project.description || "");
      setTotalBudget(Number(project.total_budget).toLocaleString('en-US'));
      setStartDate(project.start_date || "");
      setEndDate(project.end_date || "");
      setStatus(project.status || "active");
      setOrganizationId(project.organization_id || "");
    } else {
      setName("");
      setDescription("");
      setTotalBudget("");
      setStartDate("");
      setEndDate("");
      setStatus("active");
      setOrganizationId(scopedOrgIds?.[0] || "");
    }
  }, [open, project]);

  const mutation = useMutation({
    mutationFn: async () => {
      const payload: any = {
        church_id: churchId,
        name,
        description: description || null,
        total_budget: parseFloat(totalBudget.replace(/,/g, '')) || 0,
        organization_id: organizationId,
        start_date: startDate || null,
        end_date: endDate || null,
        status,
        created_by: userId,
      };
      if (project) {
        const { error } = await supabase.from("project_budgets").update(payload).eq("id", project.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("project_budgets").insert(payload);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project_budgets"] });
      
      onClose();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader><SheetTitle className="font-heading">{project ? "Edit Project" : "New Project Budget"}</SheetTitle></SheetHeader>
        <div className="space-y-4">
          <div className="space-y-2"><Label>Name *</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Building Renovation" /></div>
          <div className="space-y-2"><Label>Description</Label><Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} /></div>
          {organizations.length > 0 && (
            <div className="space-y-2">
              <Label>Ministry *</Label>
              <Select value={organizationId} onValueChange={setOrganizationId}>
                <SelectTrigger><SelectValue placeholder="Select ministry" /></SelectTrigger>
                <SelectContent>
                  {(scopedOrgIds ? organizations.filter(o => scopedOrgIds.includes(o.id)) : organizations).map(o => (
                    <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Total Budget *</Label><div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span><Input type="text" inputMode="numeric" value={totalBudget} onChange={(e) => { const raw = e.target.value.replace(/[^0-9.]/g, ''); const parts = raw.split('.'); const int = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ','); setTotalBudget(parts.length > 1 ? `${int}.${parts[1]}` : int); }} placeholder="0" className="pl-7" /></div></div>
            <div className="space-y-2"><Label>Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="on_hold">On Hold</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Start Date</Label><Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} /></div>
            <div className="space-y-2"><Label>End Date</Label><Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} /></div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={() => mutation.mutateAsync()} disabled={!name || !organizationId || mutation.isPending}>
              {mutation.isPending ? "Saving..." : project ? "Update" : "Create"}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function ItemForm({ open, onClose, item, projectId, churchId }: {
  open: boolean; onClose: () => void; item: BudgetItem | null; projectId: string; churchId: string;
}) {
  const queryClient = useQueryClient();
  const [category, setCategory] = useState("");
  const [budgeted, setBudgeted] = useState("");
  const [actual, setActual] = useState("");
  const [notes, setNotes] = useState("");

  // Reset fields whenever the dialog opens or the item being edited changes
  useEffect(() => {
    if (!open) return;
    if (item) {
      setCategory(item.category || "");
      const fmt = (n: number) => n ? n.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 }) : "";
      setBudgeted(fmt(item.budgeted_amount));
      setActual(fmt(item.actual_amount));
      setNotes(item.notes || "");
    } else {
      setCategory("");
      setBudgeted("");
      setActual("");
      setNotes("");
    }
  }, [open, item]);

  const mutation = useMutation({
    mutationFn: async () => {
      const payload: any = {
        project_budget_id: projectId,
        church_id: churchId,
        category,
        budgeted_amount: parseFloat(budgeted.replace(/,/g, '')) || 0,
        actual_amount: parseFloat(actual.replace(/,/g, '')) || 0,
        notes: notes || null,
      };
      if (item) {
        const { error } = await supabase.from("project_budget_items").update(payload).eq("id", item.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("project_budget_items").insert(payload);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project_budget_items"] });
      
      onClose();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader><SheetTitle className="font-heading">{item ? "Edit Line Item" : "Add Line Item"}</SheetTitle></SheetHeader>
        <div className="space-y-4">
          <div className="space-y-2"><Label>Category *</Label><Input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="e.g. Materials, Labor" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Budgeted Amount</Label><div className="relative"><span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span><Input type="text" inputMode="numeric" placeholder="0" value={budgeted} onChange={(e) => { const raw = e.target.value.replace(/[^0-9.]/g, ''); const parts = raw.split('.'); const clean = parts.length > 1 ? `${parts[0]}.${parts[1].slice(0, 2)}` : parts[0]; const int = clean.split('.')[0].replace(/\B(?=(\d{3})+(?!\d))/g, ','); setBudgeted(parts.length > 1 ? `${int}.${parts[1].slice(0, 2)}` : int); }} className="pl-6" /></div></div>
            <div className="space-y-2"><Label>Actual Amount</Label><div className="relative"><span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span><Input type="text" inputMode="numeric" placeholder="0" value={actual} onChange={(e) => { const raw = e.target.value.replace(/[^0-9.]/g, ''); const parts = raw.split('.'); const clean = parts.length > 1 ? `${parts[0]}.${parts[1].slice(0, 2)}` : parts[0]; const int = clean.split('.')[0].replace(/\B(?=(\d{3})+(?!\d))/g, ','); setActual(parts.length > 1 ? `${int}.${parts[1].slice(0, 2)}` : int); }} className="pl-6" /></div></div>
          </div>
          <div className="space-y-2"><Label>Notes</Label><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} /></div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={() => mutation.mutateAsync()} disabled={!category || mutation.isPending}>
              {mutation.isPending ? "Saving..." : item ? "Update" : "Add"}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
